import { EventSubChatService } from '../lib/chat/EventSubChatService';
import { ConnectionState } from '../lib/chat/types';
import { type ParsedMessage } from '../lib/chat/MessageParser';
import './ChatComponent.css';
import { TwitchEmoteService } from '../lib/twitchEmotes';
import { replaceCheerTextWithIcons } from "../utils/cheerEmote";
import { TwitchViewersService, type Viewer } from '../lib/twitchViewers';
import { twitchService } from '../lib/twitch';

export class ChatComponent {
  // Static persistent service to maintain connection across tab switches
  private static persistentChatService: EventSubChatService | null = null;
  private static messageQueue: ParsedMessage[] = [];
  private static activeComponents: Set<ChatComponent> = new Set();
  private static displayedMessages: Set<string> = new Set();
  private static messageHandler: ((message: ParsedMessage) => void) | null = null;
  
  // Message deduplication - track recently sent messages to prevent duplicates
  private static recentlySentMessages: Map<string, number> = new Map();
  private static readonly SENT_MESSAGE_TIMEOUT = 10000; // 10 seconds
  private static sentMessageCleanupInterval: number | null = null;
  
  // Static user color assignments - shared across all chat components
  private static userColors: Map<string, string> = new Map();
  private static readonly USER_COLOR_PALETTE = [
    // Primary colors first - maximum visual distinction
    '#FF4444', // Bright Red
    '#4488FF', // Bright Blue  
    '#44DD44', // Bright Green
    '#FFDD44', // Bright Yellow
    '#DD44DD', // Bright Purple
    '#FF8844', // Bright Orange
    '#44DDDD', // Bright Cyan
    '#DD44AA', // Bright Pink
    '#88DD44', // Bright Lime
    '#AA44DD', // Bright Violet
    
    // Secondary colors - still distinct but softer
    '#FF6B6B', // Soft Red
    '#45B7D1', // Soft Blue
    '#96CEB4', // Soft Green
    '#FFEAA7', // Soft Yellow
    '#DDA0DD', // Soft Purple
    '#F8C471', // Soft Orange
    '#4ECDC4', // Soft Teal
    '#FADBD8', // Soft Pink
    '#82E0AA', // Soft Mint
    '#BB8FCE', // Soft Lavender
    
    // Tertiary colors - variations and pastels (only used if many users)
    '#85C1E9', // Light Blue
    '#F1948A', // Light Red
    '#A3E4D7', // Light Aqua
    '#F7DC6F', // Light Gold
    '#D7BDE2', // Light Lavender
    '#98D8C8', // Light Mint
    '#AED6F1', // Sky Blue
    '#F9E79F', // Cream
    '#D5DBDB', // Light Gray
    '#FCF3CF'  // Light Cream
  ];

  // DOM elements
  private chatMessages!: HTMLElement;
  private chatInput!: HTMLElement;
  private sendButton!: HTMLElement;
  private emoteButton!: HTMLElement;
  private emotePicker!: HTMLElement;
  private emotePickerContent!: HTMLElement;
  private totalViewersElement!: HTMLElement;
  private realViewersElement!: HTMLElement;
  private knownBotsElement!: HTMLElement;
  private commandAutocomplete!: HTMLElement;
  private mentionAutocomplete!: HTMLElement;
  private connectionStatusElement!: HTMLElement;

  // Services
  private chatService: EventSubChatService | null = null;
  private emoteService: TwitchEmoteService | null = null;
  private viewersService: TwitchViewersService | null = null;

  // State
  private viewers: Viewer[] = [];
  private allEmotesList: any[] = [];
  private recentEmotes: any[] = [];
  private metricsUpdateInterval: number | null = null;
  private cleanupFunctions: (() => void)[] = [];
  private componentId: string = '';

  // Configuration
  private readonly supportedCommands = [
    {
      command: '/announce',
      example: '/announce Your announcement message',
      description: 'Send an announcement to chat'
    },
    {
      command: '/raid',
      example: '/raid streamername',
      description: 'Start a raid to another streamer'
    }
  ];
  
  constructor(container: HTMLElement) {
    // Add marker for debugging
    container.setAttribute('data-chat-component', 'true');
    
    this.initializeDOMElements(container);
    this.initializeServices();
    this.setupEventListeners();
  }

  public async initialize(): Promise<() => void> {
    const componentId = Math.random().toString(36).substring(7);
    console.log(`[ChatComponent-${componentId}] === INITIALIZING ===`);
    console.log(`[ChatComponent] Queue: ${ChatComponent.messageQueue.length}, Active: ${ChatComponent.activeComponents.size}`);
    console.log(`[ChatComponent] Service exists: ${!!ChatComponent.persistentChatService}`);
    console.log(`[ChatComponent] Handler exists: ${!!ChatComponent.messageHandler}`);
    
    // Store component ID for debugging
    this.componentId = componentId;
    
    try {
      // Initialize persistent chat service if needed
      if (!ChatComponent.persistentChatService) {
    const accessToken = twitchService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

        ChatComponent.persistentChatService = new EventSubChatService(accessToken);
        
        // Set up the persistent message handler that routes messages intelligently
        ChatComponent.messageHandler = (message: ParsedMessage) => {
          const timestamp = new Date().toLocaleTimeString();
          console.log(`[MessageHandler ${timestamp}] Received message from ${message.username}: "${message.message}"`);
          console.log(`[MessageHandler ${timestamp}] Active components: ${ChatComponent.activeComponents.size}`);
          
          if (ChatComponent.activeComponents.size > 0) {
            console.log(`[MessageHandler ${timestamp}] Routing to active components:`, Array.from(ChatComponent.activeComponents).map((c: any) => c.componentId || 'unknown'));
            let delivered = 0;
            ChatComponent.activeComponents.forEach(component => {
              try {
                component.addMessage(message);
                delivered++;
                console.log(`[MessageHandler ${timestamp}] Delivered to component ${(component as any).componentId || 'unknown'}`);
              } catch (error) {
                console.error(`[MessageHandler ${timestamp}] Failed to deliver to component:`, error);
              }
            });
            console.log(`[MessageHandler ${timestamp}] Successfully delivered to ${delivered}/${ChatComponent.activeComponents.size} components`);
          } else {
            // No active components, queue the message
            console.log(`[MessageHandler ${timestamp}] No active components, queuing message from ${message.username}`);
            ChatComponent.messageQueue.push(message);
            
            // Limit queue size to prevent memory issues
            if (ChatComponent.messageQueue.length > 200) {
              ChatComponent.messageQueue.shift(); // Remove oldest message
            }
            console.log(`[MessageHandler ${timestamp}] Queue size: ${ChatComponent.messageQueue.length}`);
          }
        };
        
        const unsubscribeMessage = ChatComponent.persistentChatService.onMessage(ChatComponent.messageHandler);
        
        // Store the unsubscribe function (though we probably never want to unsubscribe)
        this.cleanupFunctions.push(unsubscribeMessage);
        
        // Now connect and join channel
        await ChatComponent.persistentChatService.connect();

        const currentUser = twitchService.getUser();
        if (currentUser) {
          await ChatComponent.persistentChatService.joinChannel(currentUser.login);
        }
      }

      // Set local reference and setup handlers for UI updates
      this.chatService = ChatComponent.persistentChatService;
      this.setupChatServiceHandlers();
      
      // Register this component as active
      ChatComponent.activeComponents.add(this);
      console.log(`[ChatComponent-${componentId}] REGISTERED as active. Active count: ${ChatComponent.activeComponents.size}`);
      console.log(`[ChatComponent-${componentId}] Active components:`, Array.from(ChatComponent.activeComponents).map((c: any) => c.componentId || 'unknown'));
      
      // Start periodic cleanup if this is the first component
      if (ChatComponent.activeComponents.size === 1 && !ChatComponent.sentMessageCleanupInterval) {
        ChatComponent.startSentMessageCleanup();
      }
      
      // Make this instance globally accessible for debugging
      (window as any).currentChatComponent = this;
      
      // Get current connection status and update UI
      const currentStatus = this.chatService.getConnectionStatus();
      this.updateConnectionStatus(currentStatus.state);

      // Process any messages that arrived while component was inactive
      console.log(`[ChatComponent-${componentId}] Processing ${ChatComponent.messageQueue.length} queued messages`);
      ChatComponent.messageQueue.forEach(message => this.addMessage(message));
      
      // Clear the queue since we've processed them
      if (ChatComponent.messageQueue.length > 0) {
        console.log(`[ChatComponent-${componentId}] Processed and cleared ${ChatComponent.messageQueue.length} queued messages`);
        ChatComponent.messageQueue = [];
      }

      // Initialize other services
      await this.initializeEmotes();
      await this.initializeViewers();

      console.log('ChatComponent initialized successfully');
    } catch (error) {
      console.error('Failed to initialize ChatComponent:', error);
      this.showError('Failed to initialize chat. Please refresh the page.');
    }

    return () => this.cleanup();
  }

  private initializeDOMElements(container: HTMLElement): void {
    // Get required elements
    const elements = {
      chatMessages: container.querySelector('#chatMessages'),
      chatInput: container.querySelector('#chatInput'),
      sendButton: container.querySelector('#sendButton'),
      emoteButton: container.querySelector('.emote-button'),
      totalViewersElement: container.querySelector('#chatTotalViewers'),
      realViewersElement: container.querySelector('#chatRealViewers'),
      knownBotsElement: container.querySelector('#chatKnownBots')
    };

    // Validate required elements
    for (const [key, element] of Object.entries(elements)) {
      if (!element) {
        throw new Error(`Required element not found: ${key}`);
      }
    }

    Object.assign(this, elements);

    // Create or get emote picker
    this.emotePicker = this.createEmotePicker();
    this.emotePickerContent = this.emotePicker.querySelector('.emote-picker-content') || this.createEmotePickerContent();

    // Create autocomplete elements
    this.commandAutocomplete = this.createAutocompleteElement('commands');
    this.mentionAutocomplete = this.createAutocompleteElement('mentions');

    // Create connection status indicator
    this.connectionStatusElement = this.createConnectionStatusElement();
  }

  private createEmotePicker(): HTMLElement {
    let picker = document.querySelector('body > .emote-picker') as HTMLElement;
    if (!picker) {
      picker = document.createElement('div');
      picker.className = 'emote-picker';
      picker.style.display = 'none';
      picker.innerHTML = `
        <div class="emote-picker-header">
          <div class="emote-picker-title">
            <span>Emotes</span>
            <button class="emote-picker-close">×</button>
          </div>
          <input type="text" class="emote-search" placeholder="Search emotes...">
        </div>
        <div class="recent-emotes">
          <div class="recent-emotes-title">Recently Used</div>
          <div class="recent-emotes-grid"></div>
        </div>
        <div class="emote-picker-content"></div>
      `;
      document.body.appendChild(picker);
    }
    return picker;
  }

  private createEmotePickerContent(): HTMLElement {
      const content = document.createElement('div');
      content.className = 'emote-picker-content';
    this.emotePicker.appendChild(content);
      return content;
  }

  private createAutocompleteElement(type: string): HTMLElement {
      const autocomplete = document.createElement('div');
    autocomplete.className = `chat-autocomplete ${type}`;
    this.chatInput.parentElement?.insertBefore(autocomplete, this.chatInput);
      return autocomplete;
  }

  private createConnectionStatusElement(): HTMLElement {
    const statusElement = document.createElement('div');
    statusElement.className = 'chat-connection-status';
    statusElement.innerHTML = `
      <div class="connection-indicator">
        <span class="connection-dot disconnected"></span>
        <span class="connection-text">Disconnected</span>
      </div>
    `;
    this.chatMessages.parentElement?.insertBefore(statusElement, this.chatMessages);
    return statusElement;
  }

  private initializeServices(): void {
    const accessToken = twitchService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    this.viewersService = new TwitchViewersService(
      import.meta.env.VITE_TWITCH_CLIENT_ID,
      accessToken
    );
  }

  private setupChatServiceHandlers(): void {
    if (!this.chatService) return;

    // Only setup connection state and error handlers
    // Messages are handled by the static persistent handler
    const unsubscribeState = this.chatService.onConnectionStateChange((state) => {
      this.updateConnectionStatus(state);
    });

    const unsubscribeError = this.chatService.onError((error) => {
      this.showError(`Chat error: ${error.message}`);
    });

    // Store cleanup functions
    this.cleanupFunctions.push(unsubscribeState, unsubscribeError);
  }

  private setupEventListeners(): void {
    // Chat input handlers
    this.chatInput.addEventListener('keydown', this.handleChatInputKeydown.bind(this));
    this.chatInput.addEventListener('input', this.handleChatInputChange.bind(this));
    this.chatInput.addEventListener('paste', this.handleChatInputPaste.bind(this));

    // Send button
    this.sendButton.addEventListener('click', this.handleSendMessage.bind(this));

    // Emote picker
    this.emoteButton.addEventListener('click', this.handleEmoteButtonClick.bind(this));
    this.setupEmotePickerHandlers();

    // Autocomplete
    this.setupAutocompleteHandlers();

    // Close modals on outside click
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  private async handleChatInputKeydown(e: KeyboardEvent): Promise<void> {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
      await this.handleAutocompleteOrSend();
      } else if (e.key === 'Escape') {
      this.closeAutocomplete();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      this.handleAutocompleteNavigation(e);
      } else if (e.key === 'Tab') {
        e.preventDefault();
      this.handleAutocompleteSelection();
    }
  }

  private handleChatInputChange(): void {
      const text = this.chatInput.textContent || '';
      
      if (text.startsWith('/')) {
      this.showCommandAutocomplete(text);
      } else {
        this.commandAutocomplete.classList.remove('active');
      }

      const words = text.split(' ');
      const lastWord = words[words.length - 1];
      if (lastWord.startsWith('@')) {
      this.showMentionAutocomplete(lastWord.slice(1));
        } else {
          this.mentionAutocomplete.classList.remove('active');
        }
      }

  private handleChatInputPaste(e: ClipboardEvent): void {
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain');
      if (text) {
        document.execCommand('insertText', false, text);
      }
  }

  private async handleSendMessage(): Promise<void> {
      await this.sendMessage();
  }

  private async sendMessage(): Promise<void> {
    const message = this.extractMessageFromInput();
    if (!message.trim() || !this.chatService) return;

    const currentUser = twitchService.getUser();
    if (!currentUser) return;

    try {
      // Handle test announcement command for debugging
      if (message.startsWith('/test-announce ')) {
        const announcementMessage = message.substring(15);
        if (announcementMessage) {
          // Create a fake announcement message for testing
          const testMessage: ParsedMessage = {
            username: currentUser.login,
            displayName: currentUser.display_name || currentUser.login,
            message: announcementMessage,
            timestamp: Date.now(),
            badges: [],
            isAnnouncement: true,
            announcementColor: 'primary',
            messageType: 'announcement'
          };
          console.log('Creating test announcement:', testMessage);
          this.addMessage(testMessage);
          this.clearInput();
        }
        return;
      }

      // Handle announcement command
      if (message.startsWith('/announce ')) {
        const announcementMessage = message.substring(10);
        if (announcementMessage) {
          await this.chatService.sendAnnouncement(currentUser.login, announcementMessage);
          this.clearInput();
        }
        return;
      }

      // Handle raid command
      if (message.startsWith('/raid ')) {
        const targetStreamer = message.substring(6).trim();
        if (targetStreamer) {
          try {
            // First, get the target streamer's user ID
            const response = await fetch(`https://api.twitch.tv/helix/users?login=${encodeURIComponent(targetStreamer)}`, {
              headers: {
                'Authorization': `Bearer ${twitchService.getAccessToken()}`,
                'Client-Id': import.meta.env.VITE_TWITCH_CLIENT_ID,
              },
            });

            if (!response.ok) {
              throw new Error(`Failed to find streamer: ${response.statusText}`);
            }

            const userData = await response.json();
            if (!userData.data || userData.data.length === 0) {
              throw new Error(`Streamer "${targetStreamer}" not found`);
            }

            const targetUserId = userData.data[0].id;
            
            // Start the raid using the viewers service
            if (this.viewersService) {
              await this.viewersService.startRaid(currentUser.id, targetUserId);
              
              // Show success message in chat
              const raidMessage: ParsedMessage = {
                username: 'System',
                displayName: 'System',
                message: `🎯 Starting raid to ${targetStreamer}!`,
                timestamp: Date.now(),
                badges: [],
                messageType: 'system'
              };
              this.addMessage(raidMessage);
            } else {
              throw new Error('Viewers service not available');
            }
            
            this.clearInput();
          } catch (error) {
            console.error('Failed to start raid:', error);
            this.showError(`Failed to start raid: ${(error as Error).message}`);
          }
        } else {
          this.showError('Please specify a streamer to raid. Usage: /raid streamername');
        }
        return;
      }

      // Check connection before sending
      if (!this.chatService.isConnected()) {
        this.showError('Cannot send message. Chat is not connected.');
        return;
      }

      // Send regular message
      const success = await this.chatService.sendMessage(currentUser.login, message);
      if (success) {
        // Track this sent message to prevent duplication when it echoes back
        const messageKey = ChatComponent.createMessageKey(currentUser.login, message);
        ChatComponent.recentlySentMessages.set(messageKey, Date.now());
        
        // Clean up old sent messages
        ChatComponent.cleanupSentMessages();
        
        // Don't create local message - let it come back via EventSub to avoid duplication
        console.log('[ChatComponent] Message sent, waiting for echo from EventSub');
        this.clearInput();
      } else {
        this.showError('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      this.showError('Failed to send message. Please try again.');
    }
  }

  private extractMessageFromInput(): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.chatInput.innerHTML;
    
    let message = '';
    tempDiv.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        message += node.textContent;
      } else if (node instanceof HTMLImageElement && node.classList.contains('chat-emote')) {
        message += node.getAttribute('alt') || '';
      }
    });
    
    return message.trim();
  }

  private addMessage(message: ParsedMessage): void {
    // Enhanced logging for announcements
    if (message.isAnnouncement || message.messageType === 'announcement') {
      console.log('[ChatComponent] 🎯 Processing announcement in addMessage:', {
        username: message.username,
        messageText: message.message,
        messageType: message.messageType,
        isAnnouncement: message.isAnnouncement,
        announcementColor: message.announcementColor,
        timestamp: message.timestamp
      });
    }

    // Check if this message was recently sent by us (to prevent duplication)
    const messageKey = ChatComponent.createMessageKey(message.username, message.message);
    if (ChatComponent.recentlySentMessages.has(messageKey)) {
      const sentTime = ChatComponent.recentlySentMessages.get(messageKey)!;
      const timeSinceSent = Date.now() - sentTime;
      
      // If message was sent within the timeout period, this is likely our echo
      if (timeSinceSent < ChatComponent.SENT_MESSAGE_TIMEOUT) {
        console.log('[ChatComponent] Received echo of sent message from', message.username, ':', message.message);
        if (message.isAnnouncement) {
          console.log('[ChatComponent] 🎯 Echo is an announcement - will be displayed');
        }
        // Remove from tracking since we're about to display the echo
        // This prevents true duplicates while allowing the legitimate echo
        ChatComponent.recentlySentMessages.delete(messageKey);
        // Continue to display the message (don't return here)
      }
    }

    // Create a more specific message ID that includes the message type
    const messageId = `${message.messageType}-${message.username}-${message.timestamp}-${message.message}`;
    
    if (ChatComponent.displayedMessages.has(messageId)) {
      if (message.isAnnouncement) {
        console.log('[ChatComponent] ⚠️ Announcement already displayed, skipping:', messageId);
      }
      return;
    }
    
    ChatComponent.displayedMessages.add(messageId);

    // Limit displayedMessages size to prevent memory issues
    if (ChatComponent.displayedMessages.size > 500) {
      // Remove oldest entries (this is a simple approach)
      const entries = Array.from(ChatComponent.displayedMessages);
      const toRemove = entries.slice(0, 100); // Remove oldest 100
      toRemove.forEach(id => ChatComponent.displayedMessages.delete(id));
    }

    // Hide empty state
    const emptyChat = this.chatMessages.querySelector('.empty-chat') as HTMLElement;
    if (emptyChat) {
      emptyChat.style.display = 'none';
    }
    
    // Skip commands that shouldn't be displayed
    if (message.message.startsWith('/') && !message.isUnrecognizedCommand && !message.isAnnouncement) {
      if (message.isAnnouncement) {
        console.log('[ChatComponent] 🎯 Announcement with / prefix - NOT skipping due to isAnnouncement flag');
      }
      return;
    }

    // Enhanced logging for announcements before creating element
    if (message.isAnnouncement || message.messageType === 'announcement') {
      console.log('[ChatComponent] 🎯 About to create announcement element:', {
        isAnnouncement: message.isAnnouncement,
        messageType: message.messageType,
        announcementColor: message.announcementColor
      });
    }

    const messageElement = this.createMessageElement(message);
    this.insertMessage(messageElement);
    this.limitMessageHistory();
    this.autoScrollIfNeeded();
    
    // Final success log for announcements
    if (message.isAnnouncement || message.messageType === 'announcement') {
      console.log('[ChatComponent] ✅ Announcement successfully added to chat UI');
    }
  }

  private createMessageElement(message: ParsedMessage): HTMLElement {
    console.log('Creating message element for:', {
      messageType: message.messageType,
      isAnnouncement: message.isAnnouncement,
      announcementColor: message.announcementColor,
      text: message.message
    });

    const messageElement = document.createElement('div');
    let className = 'message';
    
    if (message.isUnrecognizedCommand) {
      className += ' unrecognized-command';
      console.log('Adding unrecognized-command class');
    } else if (message.isAnnouncement) {
      className += ` announcement ${message.announcementColor || 'primary'}`;
      console.log('🎯 Adding announcement class:', className);
    } else if (message.messageType === 'system') {
      className += ' system-message';
      console.log('Adding system-message class');
    }
    
    messageElement.className = className;
    console.log('Final className:', className);
    
    if (message.isAnnouncement) {
      console.log('🎯 Using announcement HTML template');
      messageElement.innerHTML = this.createAnnouncementHTML(message);
    } else if (message.messageType === 'system') {
      console.log('Using system message HTML template');
      messageElement.innerHTML = this.createSystemMessageHTML(message);
    } else if (message.isUnrecognizedCommand) {
      console.log('Using unrecognized command HTML template');
      messageElement.innerHTML = `<div class="message-content">${this.processMessageContent(message)}</div>`;
    } else {
      console.log('Using regular message HTML template');
      messageElement.innerHTML = this.createRegularMessageHTML(message);
    }

    // Debug log the created element for announcements
    if (message.isAnnouncement) {
      console.log('🎯 Created announcement element:', {
        className: messageElement.className,
        hasAnnouncementClass: messageElement.classList.contains('announcement'),
        innerHTML: messageElement.innerHTML.substring(0, 200) + '...'
      });
    }

    return messageElement;
  }

  private createAnnouncementHTML(message: ParsedMessage): string {
    const badgesHtml = this.createBadgesHTML(message.badges);
    const userColor = ChatComponent.getUserColor(message.username);
    return `
        <div class="announcement-header">
          <svg viewBox="0 0 24 24">
            <path d="M19.6 5.4c-.2-.2-.5-.4-.8-.4l-1.2.2c-1.2.2-2.3.7-3.2 1.4l-5.9 4.3H5c-1.7 0-3 1.3-3 3v1c0 1.7 1.3 3 3 3h.3l1.4 2.3c.3.5.9.7 1.4.7.3 0 .6-.1.9-.2.8-.5 1-1.5.5-2.3l-.5-.9h.5l5.9 4.3c.9.7 2 1.2 3.2 1.4l1.2.2c.3 0 .6-.1.8-.4.2-.2.4-.5.4-.8V6.2c0-.3-.2-.6-.4-.8zM5 15c-.6 0-1-.4-1-1v-1c0-.6.4-1 1-1h2.1l-1.7 2.7c-.1.1-.2.2-.4.3zm13 4.5c-1-.2-1.9-.6-2.7-1.1L9.4 14c-.1-.1-.3-.1-.4-.1H7.8l2.5-4h.7c.2 0 .3 0 .4-.1l5.9-4.3c.8-.6 1.7-1 2.7-1.1v15.1z"/>
          </svg>
          <span>Announcement</span>
        </div>
        <div class="message-content-wrapper">
          <div class="message-header">
            ${badgesHtml}
            <span class="username" style="color: ${userColor}">${message.displayName}</span>
          </div>
        <div class="message-content">${this.processMessageContent(message)}</div>
        </div>
      `;
  }

  private createSystemMessageHTML(message: ParsedMessage): string {
    const badgesHtml = this.createBadgesHTML(message.badges);
    const userColor = ChatComponent.getUserColor(message.username);
    return `
        <div class="system-message-header">
          <svg viewBox="0 0 24 24">
            <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
          </svg>
          <span>System</span>
        </div>
        <div class="message-content-wrapper">
          <div class="message-header">
            ${badgesHtml}
            <span class="username" style="color: ${userColor}">${message.displayName}</span>
          </div>
        <div class="message-content">${this.processMessageContent(message)}</div>
        </div>
      `;
  }

  private createRegularMessageHTML(message: ParsedMessage): string {
    const badgesHtml = this.createBadgesHTML(message.badges);
    const userColor = ChatComponent.getUserColor(message.username);
    return `
        <div class="message-header">
          ${badgesHtml}
          <span class="username" style="color: ${userColor}">${message.displayName}</span>
        </div>
      <div class="message-content">${this.processMessageContent(message)}</div>
    `;
  }

  private createBadgesHTML(badges?: any[]): string {
    if (!badges) return '';
    
    return badges.map(badge => {
      let badgeUrl;
      switch(badge.id) {
        case 'broadcaster':
          badgeUrl = 'https://static-cdn.jtvnw.net/badges/v1/5527c58c-fb7d-422d-b71b-f309dcb85cc1/3';
          break;
        case 'moderator':
          badgeUrl = 'https://static-cdn.jtvnw.net/badges/v1/3267646d-33f0-4b17-b3df-f923a41db1d0/3';
          break;
        case 'vip':
          badgeUrl = 'https://static-cdn.jtvnw.net/badges/v1/b817aba4-fad8-49e2-b88a-7cc744dfa6ec/3';
          break;
        case 'subscriber':
          const subTier = badge.version === '0' ? '1' : badge.version;
          badgeUrl = `https://static-cdn.jtvnw.net/badges/v1/5d9f2208-5dd8-11e7-8513-2ff4adfae661/${subTier}`;
          break;
        case 'partner':
          badgeUrl = 'https://static-cdn.jtvnw.net/badges/v1/d12a2e27-16f6-41d0-ab77-b780518f00a3/3';
          break;
        default:
          return '';
      }
      
      if (['broadcaster', 'moderator', 'vip', 'partner', 'subscriber'].includes(badge.id)) {
        return `<img src="${badgeUrl}" alt="${badge.id}" title="${badge.id}" class="chat-badge">`;
      }
      return '';
    }).join('');
  }

  private processMessageContent(message: ParsedMessage): string {
    let messageText = message.message;
    const words = messageText.split(' ');
    let processedMessage = '';

    // Process each word to handle emotes and maintain spaces
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const isLastWord = i === words.length - 1;

      // Skip if it's already an img tag
      if (word.startsWith('<img')) {
        processedMessage += word + (isLastWord ? '' : ' ');
        continue;
      }

      // Check for emotes from the emote service
      if (this.emoteService) {
        const emote = this.emoteService.getEmote(word);
        if (emote) {
          const emoteImg = `<img src="https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/default/dark/3.0" alt="${emote.name}" title="${emote.name}" class="chat-emote">`;
          processedMessage += emoteImg + (isLastWord ? '' : ' ');
          continue;
        }
      }

      // Handle Twitch emotes if present in the message
      if (message.emotes) {
        let isEmote = false;
        for (const [emoteId, positions] of Object.entries(message.emotes)) {
          for (const pos of positions) {
            const emoteText = messageText.slice(pos.start, pos.end + 1);
            if (emoteText === word) {
              const emoteImg = `<img src="https://static-cdn.jtvnw.net/emoticons/v2/${emoteId}/default/dark/3.0" alt="${word}" title="${word}" class="chat-emote" data-emote-id="${emoteId}">`;
              processedMessage += emoteImg + (isLastWord ? '' : ' ');
              isEmote = true;
              break;
            }
          }
          if (isEmote) break;
        }
        if (isEmote) continue;
      }

      // If not an emote, just add the word
      processedMessage += word + (isLastWord ? '' : ' ');
    }

    // Replace cheer emotes
    processedMessage = replaceCheerTextWithIcons(processedMessage);
    
    return processedMessage;
  }

  private insertMessage(messageElement: HTMLElement): void {
    if (this.chatMessages.firstChild) {
      this.chatMessages.insertBefore(messageElement, this.chatMessages.firstChild);
    } else {
      this.chatMessages.appendChild(messageElement);
    }
  }

  private limitMessageHistory(): void {
    while (this.chatMessages.children.length > 100) {
      const lastChild = this.chatMessages.lastChild;
      if (lastChild && !(lastChild as HTMLElement).classList.contains('empty-chat')) {
        this.chatMessages.removeChild(lastChild);
      } else {
        break;
      }
    }
  }

  private autoScrollIfNeeded(): void {
    const isAtBottom = this.chatMessages.scrollTop <= 50;
    if (isAtBottom) {
      requestAnimationFrame(() => {
        this.chatMessages.scrollTop = 0;
      });
    }
  }

  private updateConnectionStatus(state: ConnectionState): void {
    const statusText = this.connectionStatusElement.querySelector('.connection-text');
    const statusDot = this.connectionStatusElement.querySelector('.connection-dot');
    
    if (!statusText || !statusDot) return;

    statusDot.className = 'connection-dot';
    
    switch (state) {
      case ConnectionState.CONNECTED:
        statusText.textContent = 'Chat Connected';
        statusDot.classList.add('connected');
        break;
      case ConnectionState.CONNECTING:
        statusText.textContent = 'Connecting to Chat...';
        statusDot.classList.add('connecting');
        break;
      case ConnectionState.RECONNECTING:
        statusText.textContent = 'Reconnecting to Chat...';
        statusDot.classList.add('reconnecting');
        break;
      case ConnectionState.FAILED:
        statusText.textContent = 'Chat Connection Failed';
        statusDot.classList.add('failed');
        break;
      case ConnectionState.DISCONNECTED:
        statusText.textContent = 'Chat Disconnected';
        statusDot.classList.add('disconnected');
        break;
    }
  }

  private async initializeEmotes(): Promise<void> {
    const currentUser = twitchService.getUser();
    const accessToken = twitchService.getAccessToken();
    
    if (!currentUser || !accessToken) return;

    try {
    this.loadRecentEmotes();
    
    this.emoteService = new TwitchEmoteService(
      import.meta.env.VITE_TWITCH_CLIENT_ID,
      accessToken
    );

      const [globalEmotes, channelEmotes] = await Promise.all([
        this.emoteService.fetchGlobalEmotes(),
        this.emoteService.fetchChannelEmotes(currentUser.id)
      ]);
      
      this.allEmotesList = [...globalEmotes, ...channelEmotes];
      this.initializeEmotePicker();
      this.renderEmotes(this.allEmotesList);
    } catch (error) {
      console.error('Error loading emotes:', error);
    }
  }

  private async initializeViewers(): Promise<void> {
    const storedMetrics = localStorage.getItem('chatMetrics');
    if (storedMetrics) {
      const metrics = JSON.parse(storedMetrics);
      this.updateViewerCountsDisplay(metrics.total, metrics.real, metrics.bots);
    } else {
      this.updateViewerCountsDisplay(0, 0, 0);
    }

    await this.refreshViewers();
    this.metricsUpdateInterval = window.setInterval(() => this.refreshViewers(), 15000);
  }

  private async refreshViewers(): Promise<void> {
    const currentUser = twitchService.getUser();
    if (!currentUser || !this.viewersService) return;

    try {
      const viewers = await this.viewersService.getChannelViewers(currentUser.id);
      if (viewers && viewers.length > 0) {
        this.viewers = viewers;
        const counts = this.viewersService.getViewerCounts(this.viewers);
        this.updateViewerCountsDisplay(counts.total, counts.real, counts.bots);
        
        // Store metrics
        const metrics = { total: counts.total, real: counts.real, bots: counts.bots };
        localStorage.setItem('chatMetrics', JSON.stringify(metrics));
      }
    } catch (error) {
      this.updateViewerCountsDisplay(0, 0, 0);
    }
  }

  private updateViewerCountsDisplay(total: number, real: number, bots: number): void {
    this.totalViewersElement.textContent = total.toString();
    this.realViewersElement.textContent = real.toString();
    this.knownBotsElement.textContent = bots.toString();
  }

  private showError(message: string): void {
    console.error(message);
    // You could implement a toast notification system here
  }

  private clearInput(): void {
    this.chatInput.innerHTML = '';
  }

  private cleanup(): void {
    // Unregister this component as active
    ChatComponent.activeComponents.delete(this);
    console.log(`[ChatComponent-${this.componentId}] UNREGISTERED as active. Active count: ${ChatComponent.activeComponents.size}`);
    console.log(`[ChatComponent-${this.componentId}] Active components:`, Array.from(ChatComponent.activeComponents).map((c: any) => c.componentId || 'unknown'));
    
    // Stop periodic cleanup if this is the last component
    if (ChatComponent.activeComponents.size === 0 && ChatComponent.sentMessageCleanupInterval) {
      clearInterval(ChatComponent.sentMessageCleanupInterval);
      ChatComponent.sentMessageCleanupInterval = null;
      console.log('[ChatComponent] Sent message cleanup stopped (no active components)');
    }
    
    // Clear global reference if this is the current one
    if ((window as any).currentChatComponent === this) {
      (window as any).currentChatComponent = null;
    }
    
    // Clear intervals
    if (this.metricsUpdateInterval) {
      clearInterval(this.metricsUpdateInterval);
    }

    // Call cleanup functions
    this.cleanupFunctions.forEach(cleanup => cleanup());
    this.cleanupFunctions = [];
  }

  // Autocomplete and emote picker methods would go here...
  // (Implementing only essential methods to keep this concise)
  
  private async handleAutocompleteOrSend(): Promise<void> {
    const activeMenu = this.getActiveAutocompleteMenu();
    if (activeMenu) {
      this.handleAutocompleteSelection();
    } else {
      await this.sendMessage();
    }
  }

  private getActiveAutocompleteMenu(): HTMLElement | null {
    if (this.commandAutocomplete.classList.contains('active')) return this.commandAutocomplete;
    if (this.mentionAutocomplete.classList.contains('active')) return this.mentionAutocomplete;
    return null;
  }

  private closeAutocomplete(): void {
    this.commandAutocomplete.classList.remove('active');
    this.mentionAutocomplete.classList.remove('active');
  }

  private handleAutocompleteNavigation(e: KeyboardEvent): void {
    const activeMenu = this.getActiveAutocompleteMenu();
    if (!activeMenu) return;

    const items = Array.from(activeMenu.querySelectorAll('.chat-autocomplete-item'));
    const currentIndex = items.findIndex(item => item.classList.contains('hover'));
    let nextIndex = -1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    }

    if (nextIndex !== -1) {
      items.forEach(item => item.classList.remove('hover'));
      items[nextIndex].classList.add('hover');
      items[nextIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  private handleAutocompleteSelection(): void {
    const activeMenu = this.getActiveAutocompleteMenu();
    if (!activeMenu) return;

    const selectedItem = activeMenu.querySelector('.chat-autocomplete-item.hover') || 
                        activeMenu.querySelector('.chat-autocomplete-item');
    
    if (selectedItem) {
      // Simulate click on the selected item
      (selectedItem as HTMLElement).click();
    }
  }

  private showCommandAutocomplete(text: string): void {
    const matchingCommands = this.supportedCommands.filter(cmd => 
      cmd.command.toLowerCase().startsWith(text.toLowerCase())
    );

    if (matchingCommands.length === 0) {
      this.commandAutocomplete.classList.remove('active');
      return;
    }

    this.commandAutocomplete.innerHTML = matchingCommands.map(cmd => `
      <div class="chat-autocomplete-item" data-command="${cmd.command}">
        <span class="command">${cmd.command}</span>
        <span class="example">${cmd.example}</span>
      </div>
    `).join('');

    this.commandAutocomplete.classList.add('active');

    // Add click handlers
    this.commandAutocomplete.querySelectorAll('.chat-autocomplete-item').forEach(item => {
      item.addEventListener('click', () => {
        const command = item.getAttribute('data-command');
        if (command) {
          this.chatInput.textContent = command + ' ';
          this.chatInput.focus();
          // Place cursor at end
          const range = document.createRange();
          range.selectNodeContents(this.chatInput);
          range.collapse(false);
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
        this.commandAutocomplete.classList.remove('active');
      });
    });
  }

  private async showMentionAutocomplete(username: string): Promise<void> {
    // Get current user for channel info
    const currentUser = twitchService.getUser();
    if (!currentUser || !this.viewersService) {
      this.mentionAutocomplete.classList.remove('active');
      return;
    }

    try {
      // Refresh viewers list if empty
      if (this.viewers.length === 0) {
        this.viewers = await this.viewersService.getChannelViewers(currentUser.id);
      }

      // Filter viewers by username
      const matchingViewers = this.viewers.filter(viewer => 
        viewer.username.toLowerCase().includes(username.toLowerCase()) ||
        viewer.displayName.toLowerCase().includes(username.toLowerCase())
      ).slice(0, 5); // Limit to 5 suggestions

      if (matchingViewers.length === 0) {
        this.mentionAutocomplete.classList.remove('active');
        return;
      }

      this.mentionAutocomplete.innerHTML = matchingViewers.map(viewer => `
        <div class="chat-autocomplete-item" data-username="${viewer.username}">
          <img src="${viewer.profileImageUrl || 'https://static-cdn.jtvnw.net/user-default-pictures-uv/cdd517fe-def4-11e9-948e-784f43822e80-profile_image-70x70.png'}" 
               alt="${viewer.displayName}" 
               class="user-avatar">
          <span class="user-name">${viewer.displayName}</span>
        </div>
      `).join('');

      this.mentionAutocomplete.classList.add('active');

      // Add click handlers
      this.mentionAutocomplete.querySelectorAll('.chat-autocomplete-item').forEach(item => {
        item.addEventListener('click', () => {
          const username = item.getAttribute('data-username');
          if (username) {
            // Replace the current @mention with the selected username
            const text = this.chatInput.textContent || '';
            const words = text.split(' ');
            words[words.length - 1] = `@${username}`;
            this.chatInput.textContent = words.join(' ') + ' ';
            this.chatInput.focus();
            // Place cursor at end
            const range = document.createRange();
            range.selectNodeContents(this.chatInput);
            range.collapse(false);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
          this.mentionAutocomplete.classList.remove('active');
        });
      });
    } catch (error) {
      console.error('Error showing mention autocomplete:', error);
      this.mentionAutocomplete.classList.remove('active');
    }
  }

  private handleEmoteButtonClick(): void {
    this.emotePicker.classList.toggle('active');
  }

  private setupEmotePickerHandlers(): void {
    const searchInput = this.emotePicker.querySelector('.emote-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const searchTerm = searchInput.value.toLowerCase();
        const filteredEmotes = this.allEmotesList.filter(emote => 
          emote.name.toLowerCase().includes(searchTerm)
        );
        this.renderEmotes(filteredEmotes);
      });
    }

    const closeButton = this.emotePicker.querySelector('.emote-picker-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.emotePicker.classList.remove('active');
      });
    }

    // Initial render of all emotes
    this.renderEmotes(this.allEmotesList);
  }

  private setupAutocompleteHandlers(): void {
    // Implementation for autocomplete handlers
  }

  private handleDocumentClick(e: Event): void {
    // Don't close if clicking inside the picker, on the emote button, or on an emote item
    const target = e.target as HTMLElement;
    const isEmoteItem = target.closest('.emote-item');
    const isInsidePicker = this.emotePicker.contains(target);
    const isEmoteButton = this.emoteButton.contains(target);
    
    if (!isInsidePicker && !isEmoteButton && !isEmoteItem) {
      this.emotePicker.classList.remove('active');
    }
  }

  private loadRecentEmotes(): void {
    try {
      const saved = localStorage.getItem('recentEmotes');
      if (saved) {
        this.recentEmotes = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading recent emotes:', error);
      this.recentEmotes = [];
    }
  }

  private initializeEmotePicker(): void {
    const searchInput = this.emotePicker.querySelector('.emote-search') as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const searchTerm = searchInput.value.toLowerCase();
        const filteredEmotes = this.allEmotesList.filter(emote => 
          emote.name.toLowerCase().includes(searchTerm)
        );
        this.renderEmotes(filteredEmotes);
      });
    }

    const closeButton = this.emotePicker.querySelector('.emote-picker-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.emotePicker.classList.remove('active');
      });
    }

    // Initial render of all emotes
    this.renderEmotes(this.allEmotesList);
  }

  private renderEmotes(emotes: any[]): void {
    if (!this.emotePickerContent) return;

    // Render recent emotes first
    const recentEmotesGrid = this.emotePicker.querySelector('.recent-emotes-grid');
    if (recentEmotesGrid) {
      recentEmotesGrid.innerHTML = this.recentEmotes.map(emote => `
        <div class="emote-item recent" data-emote-name="${emote.name}" title="${emote.name}">
          <img src="https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/default/dark/1.0" 
               alt="${emote.name}" 
               loading="lazy">
        </div>
      `).join('');

      // Add click handlers for recent emotes
      recentEmotesGrid.querySelectorAll('.emote-item').forEach(item => {
        item.addEventListener('click', () => {
          const emoteName = item.getAttribute('data-emote-name');
          if (emoteName) {
            this.insertEmote(emoteName, false); // Don't update recent list when clicking a recent emote
          }
        });
      });

      // Show/hide recent emotes section based on whether there are any
      const recentEmotesSection = this.emotePicker.querySelector('.recent-emotes') as HTMLElement;
      if (recentEmotesSection) {
        recentEmotesSection.style.display = this.recentEmotes.length > 0 ? 'block' : 'none';
      }
    }

    // Render all emotes
    this.emotePickerContent.innerHTML = emotes.map(emote => `
      <div class="emote-item" data-emote-name="${emote.name}" title="${emote.name}">
        <img src="https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/default/dark/1.0" 
             alt="${emote.name}" 
             loading="lazy">
      </div>
    `).join('');

    // Add click handlers for emotes
    this.emotePickerContent.querySelectorAll('.emote-item').forEach(item => {
      item.addEventListener('click', () => {
        const emoteName = item.getAttribute('data-emote-name');
        if (emoteName) {
          this.insertEmote(emoteName, true); // Update recent list when clicking from main list
        }
      });
    });
  }

  private insertEmote(emoteName: string, updateRecent: boolean = true): void {
    const emote = this.emoteService?.getEmote(emoteName);
    if (!emote) return;

    // Only update recent emotes if specified (when clicking from main list)
    if (updateRecent) {
      this.addToRecentEmotes(emote);
    }

    // Create emote image element
    const emoteImg = document.createElement('img');
    emoteImg.src = `https://static-cdn.jtvnw.net/emoticons/v2/${emote.id}/default/dark/1.0`;
    emoteImg.alt = emoteName;
    emoteImg.title = emoteName;
    emoteImg.className = 'chat-emote';
    emoteImg.setAttribute('data-emote-id', emote.id);
    emoteImg.style.height = '20px';
    emoteImg.style.width = '20px';
    emoteImg.style.verticalAlign = '-4px';
    emoteImg.style.margin = '0 2px';
    emoteImg.style.objectFit = 'contain';
    emoteImg.style.display = 'inline-block';

    // Always focus the chat input first
    this.chatInput.focus();

    // If the input is empty, just append the emote
    if (!this.chatInput.textContent) {
      this.chatInput.appendChild(emoteImg);
      this.chatInput.appendChild(document.createTextNode(' '));
      
      // Set cursor after the emote
      const range = document.createRange();
      range.setStartAfter(this.chatInput.lastChild!);
      range.collapse(true);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    } else {
      // Get current selection or create one at the end
      const selection = window.getSelection();
      let range: Range;

      if (selection && selection.rangeCount > 0 && this.chatInput.contains(selection.getRangeAt(0).commonAncestorContainer)) {
        range = selection.getRangeAt(0);
      } else {
        // Create a range at the end
        range = document.createRange();
        const lastChild = this.chatInput.lastChild;
        if (lastChild) {
          range.setStartAfter(lastChild);
          range.setEndAfter(lastChild);
        } else {
          range.setStart(this.chatInput, 0);
          range.setEnd(this.chatInput, 0);
        }
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      // Insert the emote with proper spacing
      const spaceAfter = document.createTextNode(' ');
      const spaceBefore = document.createTextNode(
        range.startOffset === 0 || !range.startContainer.textContent ? '' : ' '
      );
      
      range.deleteContents();
      range.insertNode(spaceAfter);
      range.insertNode(emoteImg);
      range.insertNode(spaceBefore);
      
      // Move cursor to end of inserted emote
      range.setStartAfter(spaceAfter);
      range.setEndAfter(spaceAfter);
    }

    // Only re-render if we updated the recent list
    if (updateRecent) {
      this.renderEmotes(this.allEmotesList);
    }
  }

  private addToRecentEmotes(emote: any): void {
    // Remove if already in recent emotes
    this.recentEmotes = this.recentEmotes.filter(e => e.name !== emote.name);
    
    // Add to start of array
    this.recentEmotes.unshift(emote);
    
    // Keep only last 20 emotes
    if (this.recentEmotes.length > 20) {
      this.recentEmotes.pop();
    }
    
    // Save to localStorage
    localStorage.setItem('recentEmotes', JSON.stringify(this.recentEmotes));
  }

  // Test method to add a fake message
  public static addTestMessage(): void {
    const testMessage: ParsedMessage = {
      username: 'testuser',
      displayName: 'TestUser',
      message: `Test message at ${new Date().toLocaleTimeString()}`,
      timestamp: Date.now(),
      messageType: 'chat'
    };

    console.log('[ChatComponent] Adding test message');
    if (ChatComponent.messageHandler) {
      ChatComponent.messageHandler(testMessage);
    } else {
      console.error('[ChatComponent] No message handler available');
    }
  }

  // Static utility methods for managing persistent state
  public static clearMessageQueue(): void {
    ChatComponent.messageQueue = [];
    console.log('Message queue cleared');
  }

  public static clearDisplayedMessages(): void {
    ChatComponent.displayedMessages.clear();
    console.log('Displayed messages cache cleared');
  }

  public static getQueueStatus(): { queueSize: number; displayedCount: number; activeComponents: number } {
    return {
      queueSize: ChatComponent.messageQueue.length,
      displayedCount: ChatComponent.displayedMessages.size,
      activeComponents: ChatComponent.activeComponents.size
    };
  }

  // Comprehensive debug status
  public static getDebugStatus(): void {
    console.log('=== CHAT DEBUG STATUS ===');
    console.log('Service exists:', !!ChatComponent.persistentChatService);
    console.log('Message handler exists:', !!ChatComponent.messageHandler);
    console.log('Active components:', ChatComponent.activeComponents.size);
    console.log('Active component IDs:', Array.from(ChatComponent.activeComponents).map((c: any) => c.componentId || 'unknown'));
    console.log('Queue size:', ChatComponent.messageQueue.length);
    console.log('Recent queue messages:', ChatComponent.messageQueue.slice(-3).map(m => `${m.username}: ${m.message}`));
    console.log('Connection status:', ChatComponent.persistentChatService?.getConnectionStatus());
    console.log('Current component:', (window as any).currentChatComponent?.componentId || 'none');
    console.log('========================');
  }

  // Static method for health check
  public static checkChatHealth(): void {
    console.log('[ChatComponent] Performing EventSub chat health check...');
    
    if (ChatComponent.persistentChatService) {
      // Get connection status for debugging
      const status = ChatComponent.persistentChatService.getConnectionStatus();
      const now = Date.now();
      const timeSinceLastMessage = status.lastMessageTime > 0 ? now - status.lastMessageTime : 0;
      
      console.log('[ChatComponent] EventSub connection status:', {
        state: status.state,
        isConnected: status.state === ConnectionState.CONNECTED,
        lastMessageTime: status.lastMessageTime ? new Date(status.lastMessageTime).toLocaleTimeString() : 'never',
        minutesSinceLastMessage: status.lastMessageTime > 0 ? Math.round(timeSinceLastMessage / 60000) : 'n/a',
        error: status.error
      });
      
      // Check connection health
      const fiveMinutes = 5 * 60 * 1000;
      
      if (status.state !== ConnectionState.CONNECTED) {
        console.warn('[ChatComponent] EventSub is disconnected - connection issue');
      } else {
        console.log('[ChatComponent] EventSub connection is healthy');
      }
      
      if (status.lastMessageTime > 0 && timeSinceLastMessage > fiveMinutes) {
        console.warn(`[ChatComponent] No messages received in ${Math.round(timeSinceLastMessage / 60000)} minutes`);
      }
      
      // Force the health check on the EventSub service
      ChatComponent.persistentChatService.forceHealthCheck();
    } else {
      console.warn('[ChatComponent] No persistent chat service available for health check');
    }
  }

  // Manual method to trigger connection refresh (for testing)
  public static forceRefreshIfZombie(): void {
    console.log('[ChatComponent] Manually checking EventSub connection status...');
    
    if (ChatComponent.persistentChatService) {
      const status = ChatComponent.persistentChatService.getConnectionStatus();
      const now = Date.now();
      const timeSinceLastMessage = status.lastMessageTime > 0 ? now - status.lastMessageTime : 0;
      const tenMinutes = 10 * 60 * 1000;
      
      console.log('[ChatComponent] Current status:', {
        isConnected: status.state === ConnectionState.CONNECTED,
        lastMessage: status.lastMessageTime ? new Date(status.lastMessageTime).toLocaleString() : 'never',
        minutesSinceLastMessage: status.lastMessageTime > 0 ? Math.round(timeSinceLastMessage / 60000) : 'n/a'
      });
      
      if (status.state !== ConnectionState.CONNECTED) {
        console.warn('[ChatComponent] EventSub disconnected - refreshing page...');
        window.location.reload();
      } else if (status.lastMessageTime > 0 && timeSinceLastMessage > tenMinutes) {
        console.warn(`[ChatComponent] No messages in ${Math.round(timeSinceLastMessage / 60000)} minutes - possible connection issue`);
        console.warn('[ChatComponent] Consider refreshing if chat seems inactive');
      } else {
        console.log('[ChatComponent] EventSub connection appears healthy');
      }
    } else {
      console.warn('[ChatComponent] No persistent chat service available');
    }
  }

  // Get comprehensive connection diagnostics
  public static getConnectionDiagnostics(): void {
    console.log('=== EVENTSUB CHAT CONNECTION DIAGNOSTICS ===');
    
    if (!ChatComponent.persistentChatService) {
      console.error('❌ No persistent chat service available');
      return;
    }
    
    const status = ChatComponent.persistentChatService.getConnectionStatus();
    const now = Date.now();
    const timeSinceLastMessage = status.lastMessageTime > 0 ? now - status.lastMessageTime : 0;
    
    console.log('🔌 Overall State:', status.state);
    console.log('🌐 EventSub Connected:', status.state === ConnectionState.CONNECTED ? '✅ Yes' : '❌ No');
    console.log('📩 Last Message:', status.lastMessageTime ? new Date(status.lastMessageTime).toLocaleString() : 'Never');
    console.log('🕐 Minutes Since Message:', status.lastMessageTime > 0 ? Math.round(timeSinceLastMessage / 60000) : 'N/A');
    console.log('⚠️  Connection Error:', status.error || 'None');
    
    // Health assessment
    const isHealthy = status.state === ConnectionState.CONNECTED;
    
    console.log('🏥 Overall Health:', isHealthy ? '✅ Connected' : '❌ Disconnected');
    console.log('📊 Message Flow:', status.lastMessageTime > 0 ? '✅ Active' : '⚠️ No messages yet');
    console.log('ℹ️  Mode: EventSub + API (modern Twitch chat)');
    
    console.log('=== END DIAGNOSTICS ===');
  }

  // Simplified message flow test for EventSub
  public static testEventSubMessageFlow(): void {
    console.log('=== TESTING EVENTSUB MESSAGE FLOW ===');
    console.log('1. Check current connection status...');
    ChatComponent.getConnectionDiagnostics();
    
    console.log('2. Send a message in chat to test the flow');
    console.log('3. Look for EventSub message logs in console');
    console.log('4. Both sending (API) and receiving (EventSub) should work');
    console.log('=== TEST READY ===');
  }

  // EventSub-specific diagnostics
  public static getEventSubDiagnostics(): void {
    if (ChatComponent.persistentChatService && typeof ChatComponent.persistentChatService.forceHealthCheck === 'function') {
      ChatComponent.persistentChatService.forceHealthCheck();
    } else {
      console.warn('[ChatComponent] EventSub diagnostics not available');
    }
  }

  /**
   * Assigns a color to a user based on their username
   * Uses a hash function to consistently assign the same color to the same user
   */
  private static assignUserColor(username: string): string {
    // Check if user already has a color assigned
    if (ChatComponent.userColors.has(username)) {
      return ChatComponent.userColors.get(username)!;
    }

    // Simple hash function to consistently assign colors
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      const char = username.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Use absolute value and modulo to get index
    const colorIndex = Math.abs(hash) % ChatComponent.USER_COLOR_PALETTE.length;
    const color = ChatComponent.USER_COLOR_PALETTE[colorIndex];
    
    // Store the assignment
    ChatComponent.userColors.set(username, color);
    
    return color;
  }

  /**
   * Gets the assigned color for a user
   */
  private static getUserColor(username: string): string {
    return ChatComponent.assignUserColor(username);
  }

  // Static utility methods for message deduplication
  private static createMessageKey(username: string, messageText: string): string {
    return `${username.toLowerCase()}:${messageText.trim()}`;
  }

  private static cleanupSentMessages(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    ChatComponent.recentlySentMessages.forEach((sentTime, key) => {
      if (now - sentTime > ChatComponent.SENT_MESSAGE_TIMEOUT) {
        expiredKeys.push(key);
      }
    });
    
    expiredKeys.forEach(key => ChatComponent.recentlySentMessages.delete(key));
    
    if (expiredKeys.length > 0) {
      console.log(`[ChatComponent] Cleaned up ${expiredKeys.length} expired sent message(s)`);
    }
  }

  // Static method to start periodic sent message cleanup
  private static startSentMessageCleanup(): void {
    if (ChatComponent.sentMessageCleanupInterval) return;

    ChatComponent.sentMessageCleanupInterval = setInterval(() => {
      ChatComponent.cleanupSentMessages();
    }, ChatComponent.SENT_MESSAGE_TIMEOUT);

    console.log('[ChatComponent] Sent message cleanup started');
  }

  // Test the simplified EventSub + API architecture
  public static testSimplifiedChat(): void {
    console.log('🎉 === SIMPLIFIED EVENTSUB + API CHAT SYSTEM ===');
    console.log('✅ Removed: IRC, Hybrid system, Complex fallback logic');
    console.log('✅ Kept: EventSub for receiving, API for sending');
    console.log('');
    console.log('🧪 Test the system:');
    console.log('1. Check status: ChatComponent.getConnectionDiagnostics()');
    console.log('2. Send message: Type in chat and press Enter');
    console.log('3. Send announcement: /announce Hello EventSub!');
    console.log('4. Check flow: ChatComponent.testEventSubMessageFlow()');
    console.log('');
    console.log('💡 Benefits:');
    console.log('- Much simpler codebase');
    console.log('- Modern Twitch API usage');
    console.log('- No IRC complexity');
    console.log('- Reliable EventSub receiving');
    console.log('- API-based sending with proper error handling');
    console.log('=== READY TO TEST ===');
  }
}
